import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Typography, Card, List, Tag, Space, message as antMessage, Spin, Radio, Checkbox, Select, DatePicker, Avatar, Switch, Slider } from 'antd';
import { Sparkles, User, Upload as UploadIcon, Send, Paperclip, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import FileUploadModal from './FileUploadModal';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const ChatComponentV2 = ({
  data,
  title = "Agent Conversation",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  onSendMessage,
  onFileUpload,
  onComponentClick,
  onInputSubmit,
  componentRefs = {},
  isLoading = false,
  sessionId,
  agent_key,
  project_id,
  component_map
}) => {
  // State
  const [isExpanded, setIsExpanded] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [currentUploadRequest, setCurrentUploadRequest] = useState(null);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isStable, setIsStable] = useState(false);
  const messagesEndRef = useRef(null);
  const messageToSendRef = useRef('');

  // State for input widgets
  const [inputValues, setInputValues] = useState({});
  const [inputValidation, setInputValidation] = useState({});
  const [submittingInputs, setSubmittingInputs] = useState({});

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsStable(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current && isStable) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages, isStable]);

  // Input handling functions
  const generateInputId = (messageIndex, inputConfig) => {
    return `input_${messageIndex}_${inputConfig.id || 'default'}`;
  };

  const handleInputChange = (inputId, value) => {
    setInputValues(prev => ({
      ...prev,
      [inputId]: value
    }));

    if (inputValidation[inputId]) {
      setInputValidation(prev => ({
        ...prev,
        [inputId]: null
      }));
    }
  };

  const validateInput = (inputConfig, value) => {
    const { required, validationRules = {} } = inputConfig;

    if (required && (value === undefined || value === null || value === '')) {
      return 'This field is required';
    }

    if (validationRules.minLength && value?.length < validationRules.minLength) {
      return `Minimum length is ${validationRules.minLength}`;
    }

    if (validationRules.maxLength && value?.length > validationRules.maxLength) {
      return `Maximum length is ${validationRules.maxLength}`;
    }

    if (inputConfig.widget === 'checkbox' && inputConfig.minSelections && value?.length < inputConfig.minSelections) {
      return `Please select at least ${inputConfig.minSelections} option(s)`;
    }

    return null;
  };

  const handleInputSubmitInternal = async (messageIndex, inputConfig) => {
    const inputId = generateInputId(messageIndex, inputConfig);
    const value = inputValues[inputId];

    const validationError = validateInput(inputConfig, value);
    if (validationError) {
      setInputValidation(prev => ({
        ...prev,
        [inputId]: validationError
      }));
      return;
    }

    try {
      setSubmittingInputs(prev => ({ ...prev, [inputId]: true }));

      const success = await onInputSubmit({
        inputId: inputConfig.id || inputId,
        inputConfig,
        value,
        messageIndex,
        uuid,
        sessionId,
        agent_key,
        project_id
      });

      if (success) {
        setInputValues(prev => {
          const newValues = { ...prev };
          delete newValues[inputId];
          return newValues;
        });
        setInputValidation(prev => {
          const newValidation = { ...prev };
          delete newValidation[inputId];
          return newValidation;
        });
        antMessage.success('Input submitted successfully');
      } else {
        setSubmittingInputs(prev => {
          const newSubmitting = { ...prev };
          delete newSubmitting[inputId];
          return newSubmitting;
        });
        antMessage.error('Failed to submit input');
      }
    } catch (error) {
      console.error('Error submitting input:', error);
      antMessage.error(`Error: ${error.message}`);
      setSubmittingInputs(prev => {
        const newSubmitting = { ...prev };
        delete newSubmitting[inputId];
        return newSubmitting;
      });
    }
  };

  // Render input widget types
  const renderInputWidget = (messageIndex, inputConfig) => {
    const inputId = generateInputId(messageIndex, inputConfig);
    const value = inputValues[inputId];
    const validationError = inputValidation[inputId];
    const isSubmitting = submittingInputs[inputId];

    const commonProps = {
      disabled: isSubmitting,
      style: { width: '100%' }
    };

    const renderWidget = () => {
      switch (inputConfig.widget) {
        case 'radio':
          return (
            <Radio.Group
              {...commonProps}
              value={value}
              onChange={(e) => handleInputChange(inputId, e.target.value)}
            >
              <Space direction="vertical">
                {inputConfig.options.map((option, idx) => (
                  <Radio key={idx} value={option.value || option}>
                    {option.label || option}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          );

        case 'checkbox':
          if (inputConfig.single) {
            return (
              <Checkbox
                {...commonProps}
                checked={value || false}
                onChange={(e) => handleInputChange(inputId, e.target.checked)}
              >
                {inputConfig.label || 'Confirm'}
              </Checkbox>
            );
          } else {
            return (
              <Checkbox.Group
                {...commonProps}
                value={value || []}
                onChange={(checkedValues) => handleInputChange(inputId, checkedValues)}
              >
                <Space direction="vertical">
                  {inputConfig.options.map((option, idx) => (
                    <Checkbox key={idx} value={option.value || option}>
                      {option.label || option}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            );
          }

        case 'dropdown':
          return (
            <Select
              {...commonProps}
              value={value}
              placeholder={inputConfig.placeholder || "Please select..."}
              onChange={(selectedValue) => handleInputChange(inputId, selectedValue)}
              allowClear
            >
              {inputConfig.options.map((option, idx) => (
                <Option key={idx} value={option.value || option}>
                  {option.label || option}
                </Option>
              ))}
            </Select>
          );

        case 'date':
          return (
            <DatePicker
              {...commonProps}
              value={value}
              placeholder={inputConfig.placeholder || "Select date"}
              onChange={(date) => handleInputChange(inputId, date)}
              format={inputConfig.dateFormat || 'YYYY-MM-DD'}
            />
          );

        case 'single_select_card':
          return (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '12px',
              width: '100%'
            }}>
              {inputConfig.options.map((option, idx) => {
                const isSelected = value === option;
                return (
                  <Card
                    key={idx}
                    hoverable
                    style={{
                      border: isSelected ? '2px solid #6200EE' : '1px solid #d9d9d9',
                      backgroundColor: isSelected ? '#f6f0ff' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      height: 'fit-content'
                    }}
                    onClick={() => handleInputChange(inputId, option)}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          fontSize: '14px', 
                          marginBottom: '6px',
                          color: '#333',
                          lineHeight: '1.2'
                        }}>
                          {option.title}
                        </div>
                        {option.line1 && (
                          <div style={{ marginBottom: '3px', color: '#666', fontSize: '12px', lineHeight: '1.3' }}>
                            {option.line1}
                          </div>
                        )}
                        {option.line2 && (
                          <div style={{ marginBottom: '3px', color: '#666', fontSize: '12px', lineHeight: '1.3' }}>
                            {option.line2}
                          </div>
                        )}
                        {option.line3 && (
                          <div style={{ marginBottom: '3px', color: '#666', fontSize: '12px', lineHeight: '1.3' }}>
                            {option.line3}
                          </div>
                        )}
                        {option.line4 && (
                          <div style={{ color: '#666', fontSize: '12px', lineHeight: '1.3' }}>
                            {option.line4}
                          </div>
                        )}
                      </div>
                      <Radio
                        checked={isSelected}
                        style={{ marginLeft: '8px', flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </Card>
                );
              })}
            </div>
          );

        case 'form':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {inputConfig.form_body.map((element, idx) => {
                const elementId = `${element.type}_${idx}`;
                const elementValue = value?.[elementId];

                switch (element.type) {
                  case 'label':
                    return (
                      <div key={idx} style={{ fontSize: '16px', fontWeight: '500', color: '#333' }}>
                        {element.value}
                      </div>
                    );

                  case 'text':
                    return (
                      <Input
                        key={idx}
                        value={elementValue || ''}
                        placeholder={element.placeholder || 'Enter text...'}
                        onChange={(e) => {
                          const newFormValue = { ...value, [elementId]: e.target.value };
                          handleInputChange(inputId, newFormValue);
                        }}
                        disabled={isSubmitting}
                      />
                    );

                  case 'textarea':
                    return (
                      <TextArea
                        key={idx}
                        value={elementValue || ''}
                        placeholder={element.placeholder || 'Enter your response...'}
                        rows={4}
                        onChange={(e) => {
                          const newFormValue = { ...value, [elementId]: e.target.value };
                          handleInputChange(inputId, newFormValue);
                        }}
                        disabled={isSubmitting}
                      />
                    );

                  case 'checkbox':
                    return (
                      <div key={idx}>
                        <Checkbox.Group
                          value={elementValue || []}
                          onChange={(checkedValues) => {
                            const newFormValue = { ...value, [elementId]: checkedValues };
                            handleInputChange(inputId, newFormValue);
                          }}
                          disabled={isSubmitting}
                        >
                          <Space direction="vertical">
                            {(Array.isArray(element.value) ? element.value : [element.value]).map((option, optIdx) => (
                              <Checkbox key={optIdx} value={option}>
                                {option}
                              </Checkbox>
                            ))}
                          </Space>
                        </Checkbox.Group>
                      </div>
                    );

                  case 'radio':
                    return (
                      <div key={idx}>
                        <Radio.Group
                          value={elementValue}
                          onChange={(e) => {
                            const newFormValue = { ...value, [elementId]: e.target.value };
                            handleInputChange(inputId, newFormValue);
                          }}
                          disabled={isSubmitting}
                        >
                          <Space direction="vertical">
                            {(Array.isArray(element.value) ? element.value : [element.value]).map((option, optIdx) => (
                              <Radio key={optIdx} value={option}>
                                {option}
                              </Radio>
                            ))}
                          </Space>
                        </Radio.Group>
                      </div>
                    );

                  case 'date':
                    return (
                      <DatePicker
                        key={idx}
                        value={elementValue}
                        placeholder="Select date"
                        onChange={(date) => {
                          const newFormValue = { ...value, [elementId]: date };
                          handleInputChange(inputId, newFormValue);
                        }}
                        disabled={isSubmitting}
                        style={{ width: '100%' }}
                      />
                    );

                  case 'toggle':
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch
                          checked={elementValue || false}
                          onChange={(checked) => {
                            const newFormValue = { ...value, [elementId]: checked };
                            handleInputChange(inputId, newFormValue);
                          }}
                          disabled={isSubmitting}
                        />
                        <span>{element.label || element.value || 'Toggle'}</span>
                      </div>
                    );

                  case 'slider':
                    return (
                      <div key={idx}>
                        {element.label && (
                          <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                            {element.label}: {elementValue || element.min || 0}
                          </div>
                        )}
                        <Slider
                          value={elementValue || element.min || 0}
                          min={element.min || 0}
                          max={element.max || 100}
                          step={element.step || 1}
                          onChange={(sliderValue) => {
                            const newFormValue = { ...value, [elementId]: sliderValue };
                            handleInputChange(inputId, newFormValue);
                          }}
                          disabled={isSubmitting}
                        />
                      </div>
                    );

                  case 'button':
                    return (
                      <Button
                        key={idx}
                        type={element.type === 'primary' || element.value === 'Proceed' ? 'primary' : 'default'}
                        danger={element.value === 'Stop' || element.danger}
                        onClick={async () => {
                          try {
                            setSubmittingInputs(prev => ({ ...prev, [inputId]: true }));

                            const success = await onInputSubmit({
                              inputId: inputConfig.id || inputId,
                              inputConfig,
                              value: {
                                formData: value || {},
                                buttonPressed: element.value
                              },
                              messageIndex,
                              uuid,
                              sessionId,
                              agent_key,
                              project_id
                            });

                            if (success) {
                              setInputValues(prev => {
                                const newValues = { ...prev };
                                delete newValues[inputId];
                                return newValues;
                              });
                              setInputValidation(prev => {
                                const newValidation = { ...prev };
                                delete newValidation[inputId];
                                return newValidation;
                              });
                              antMessage.success(`Form submitted: ${element.value}`);
                            } else {
                              setSubmittingInputs(prev => {
                                const newSubmitting = { ...prev };
                                delete newSubmitting[inputId];
                                return newSubmitting;
                              });
                              antMessage.error('Failed to submit form');
                            }
                          } catch (error) {
                            console.error('Error submitting form:', error);
                            antMessage.error(`Error: ${error.message}`);
                            setSubmittingInputs(prev => {
                              const newSubmitting = { ...prev };
                              delete newSubmitting[inputId];
                              return newSubmitting;
                            });
                          }
                        }}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: element.value === 'Proceed' ? '#6200EE' : undefined,
                          borderColor: element.value === 'Proceed' ? '#6200EE' : undefined
                        }}
                      >
                        {element.value}
                      </Button>
                    );

                  default:
                    return (
                      <div key={idx} style={{ color: '#ff4d4f' }}>
                        Unknown form element type: {element.type}
                      </div>
                    );
                }
              })}
            </div>
          );

        default:
          return <Text type="danger">Unknown widget type: {inputConfig.widget}</Text>;
      }
    };

    return (
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px', 
        margin: '8px 0',
        border: '1px solid #e9ecef'
      }}>
        {inputConfig.description && (
          <div style={{ marginBottom: '12px' }}>
            <Text type="secondary">{inputConfig.description}</Text>
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          {renderWidget()}
        </div>

        {validationError && (
          <div style={{ marginBottom: '12px' }}>
            <Text type="danger" style={{ fontSize: '12px' }}>
              {validationError}
            </Text>
          </div>
        )}

        {inputConfig.widget !== 'form' && (
          <Button
            type="primary"
            onClick={() => handleInputSubmitInternal(messageIndex, inputConfig)}
            loading={isSubmitting}
            disabled={isSubmitting}
            style={{ 
              backgroundColor: '#6200EE',
              borderColor: '#6200EE'
            }}
          >
            Submit
          </Button>
        )}
      </div>
    );
  };

  // Render input response
  const renderInputResponse = (inputResponse) => {
    return (
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#f0f9f0', 
        borderRadius: '8px', 
        margin: '8px 0',
        border: '1px solid #d9f7be',
        display: 'flex',
        alignItems: 'center'
      }}>
        <CheckCircle size={16} color="#52c41a" style={{ marginRight: '8px' }} />
        <Text style={{ color: '#389e0d' }}>
          {inputResponse.displayText || `Selected: ${inputResponse.value}`}
        </Text>
      </div>
    );
  };

  // Enhanced message rendering
  const renderMessageContent = (message, messageIndex) => {
    if (message.messageType === 'chatInputRequest' && message.inputConfig) {
      return (
        <div>
          {message.content && (
            <div style={{ marginBottom: '8px' }}>
              <ReactMarkdown components={{
                p: ({ children }) => <div style={{ margin: '0 0 10px 0' }}>{children}</div>,
                strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>
              }}>
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {renderInputWidget(messageIndex, message.inputConfig)}
        </div>
      );
    }

    if (message.messageType === 'chatInputResponse' && message.inputResponse) {
      return (
        <div>
          {message.originalPrompt && (
            <div style={{ marginBottom: '8px' }}>
              <Text>{message.originalPrompt}</Text>
            </div>
          )}
          {renderInputResponse(message.inputResponse)}
        </div>
      );
    }

    return (
      <div style={{ maxWidth: '100%', overflow: 'hidden' }}>
        <ReactMarkdown components={{
          p: ({ children }) => <div style={{ margin: '0 0 10px 0' }}>{children}</div>,
          h1: ({ children }) => <div style={{ fontSize: '24px', marginTop: '20px', marginBottom: '10px' }}>{children}</div>,
          h2: ({ children }) => <div style={{ fontSize: '20px', marginTop: '16px', marginBottom: '8px' }}>{children}</div>,
          h3: ({ children }) => <div style={{ fontSize: '18px', marginTop: '14px', marginBottom: '7px' }}>{children}</div>,
          strong: ({ children }) => <strong style={{ fontWeight: 'bold' }}>{children}</strong>,
          em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
          ul: ({ children }) => <ul style={{ marginLeft: '5px', marginBottom: '10px' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ marginLeft: '5px', marginBottom: '10px' }}>{children}</ol>,
          li: ({ children }) => <li style={{ margin: '2px 0' }}>{children}</li>,
          code: ({ children }) => (
            <code style={{
              backgroundColor: '#f5f5f5',
              padding: '2px 4px',
              borderRadius: '3px',
              fontFamily: 'monospace'
            }}>
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '5px',
              overflow: 'auto',
              marginBottom: '10px',
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {children}
            </pre>
          )
        }}>
          {message.content}
        </ReactMarkdown>
      </div>
    );
  };

  // Version change handler
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  // File upload click handler
  const handleFileUploadClick = (uploadRequest) => {
    setCurrentUploadRequest(uploadRequest);
    setUploadModalVisible(true);
  };

  // Upload modal close handler
  const handleUploadModalClose = (success) => {
    setUploadModalVisible(false);
    if (success === false && currentUploadRequest) {
      onSendMessage(`I've cancelled the upload for ${currentUploadRequest.title || 'files'}.`, uuid, null, agent_key);
    }
    setCurrentUploadRequest(null);
  };

  // File upload handler
  const handleFileUpload = async (files, context, sessionId) => {
    try {
      setUploadProcessing(true);
      if (!onFileUpload) {
        throw new Error('File upload handler not provided');
      }
      const success = await onFileUpload(files, uuid, context, sessionId);
      return success;
    } catch (error) {
      console.error('Error uploading files:', error);
      return false;
    } finally {
      setUploadProcessing(false);
    }
  };

  // Send message handler
  const handleSendMessage = async () => {
    if (newMessage.trim() && onSendMessage && !sendingMessage) {
      try {
        setSendingMessage(true);
        messageToSendRef.current = newMessage;
        setNewMessage('');
        const success = await onSendMessage(messageToSendRef.current, uuid, null, agent_key, project_id);
        if (!success) {
          antMessage.error('Failed to send message');
          setNewMessage(messageToSendRef.current);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        antMessage.error(`Error: ${error.message}`);
        if (newMessage.trim() === '') {
          setNewMessage(messageToSendRef.current || '');
        }
      } finally {
        setSendingMessage(false);
      }
    }
  };

  // Key press handler
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Component click handler
  const handleComponentClick = (componentRef) => {
    if (!componentRef) return;
    if (sendingMessage) return;

    console.log("Component reference clicked:", componentRef);

    try {
      if (componentRef.type === 'fileUploadRequest') {
        const uploadRequest = { ...componentRef };
        handleFileUploadClick(uploadRequest);
        return;
      }

      if (onComponentClick) {
        onComponentClick(componentRef);
        return;
      }

      if (onVersionChange && componentRef.uuid) {
        const version = componentRef.version || 1;
        onVersionChange(componentRef.uuid, version);
        antMessage.info(`Loading ${componentRef.title || 'component'}...`);
      } else {
        antMessage.warning(`Couldn't navigate to ${componentRef.title || 'component'}`);
      }
    } catch (error) {
      console.error('Error navigating to component:', error);
      antMessage.error('Error navigating to component');
    }
  };

  // Render component reference
  const renderComponentReference = (componentRef) => {
    const { componentKey, componentType, title, type } = componentRef;

    if (type === 'fileUploadRequest') {
      return (
        <button 
          style={{ 
            margin: '4px',
            backgroundColor: '#e6f7ff',
            border: 'none',
            borderLeft: '3px solid #1890ff',
            borderRadius: '4px',
            padding: '8px 16px',
            fontWeight: 'normal',
            color: '#000',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={() => handleComponentClick(componentRef)}
        >
          <Paperclip size={16} style={{ marginRight: '8px' }} />
          {title || 'Upload Files'}
        </button>
      );
    }

    let buttonStyle = { 
      margin: '4px',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      fontWeight: 'normal',
      color: '#000',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    };

    if (componentType === 'list_component') {
      buttonStyle.backgroundColor = '#e0f7fa';
      buttonStyle.borderLeft = '3px solid #00b0ff';
    } else if (componentType === 'search_results') {
      buttonStyle.backgroundColor = '#f1f8e9';
      buttonStyle.borderLeft = '3px solid #7cb342';
    } else {
      buttonStyle.backgroundColor = '#e0e0e0';
      buttonStyle.borderLeft = '3px solid #9e9e9e';
    }

    return (
      <button 
        style={buttonStyle}
        onClick={() => handleComponentClick(componentRef)}
      >
        {title || componentKey}
      </button>
    );
  };

  // Render file reference
  const renderFileReference = (fileRef) => {
    const { fileName } = fileRef;
    return (
      <button
        style={{ 
          margin: '4px',
          backgroundColor: '#fce4ec',
          border: 'none',
          borderLeft: '3px solid #ec407a',
          borderRadius: '4px',
          padding: '8px 16px',
          fontWeight: 'normal',
          color: '#000',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
      >
        {fileName || "Uploaded File"}
      </button>
    );
  };

  // Loading state
  if (!data || !data.messages) {
    return (
      <div style={{ 
        marginBottom: '16px', 
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
        border: '1px solid #e8e8e8',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#666' }}>Loading conversation...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '8px',
      border: '1px solid #e8e8e8',
      backgroundColor: '#fff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Loading overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 100,
          borderRadius: '8px',
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '15px', fontSize: '16px', color: '#6200EE' }}>
            Generating response...
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      <FileUploadModal
        visible={uploadModalVisible}
        onCancel={handleUploadModalClose}
        onUpload={handleFileUpload}
        title={currentUploadRequest?.title || "Upload Files"}
        maxFiles={currentUploadRequest?.maxFiles || 5}
        acceptedFileTypes={currentUploadRequest?.acceptedFileTypes || "*"}
        sessionId={sessionId}
        requestContext={currentUploadRequest}
      />

      {/* Header */}
      <div 
        style={{ 
          padding: '12px 16px',
          borderBottom: '1px solid #e8e8e8',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          flexShrink: 0
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ 
          fontWeight: 'bold',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <span>{title}</span>
          {isLoading && <Spin size="small" style={{ marginLeft: '8px' }} />}
        </div>
      </div>

      {/* Content - only show when expanded */}
      {isExpanded && (
        <>
          {/* Messages area */}
          <div 
            style={{ 
              flex: 1,
              overflowY: 'auto',
              padding: '12px',
              backgroundColor: '#e5e5e5',
              minHeight: 0
            }}
          >
            <List
              itemLayout="horizontal"
              dataSource={data?.messages || []}
              renderItem={(message, index) => (
                <List.Item
                  key={index}
                  style={{ 
                    padding: '6px 0',
                    border: 'none',
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%'
                  }}
                >
                  <Card
                    style={{
                      maxWidth: '90%',
                      margin: message.role === 'user' ? '0 0 0 auto' : '0 auto 0 0',
                      backgroundColor: message.role === 'user' ? '#6200EE' : '#ffffff',
                      color: message.role === 'user' ? 'white' : 'black',
                      border: message.role === 'user' ? 'none' : '1px solid #f0f0f0'
                    }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      {message.role !== 'user' && (
                        <Avatar
                          style={{ backgroundColor: '#6200EE', flexShrink: 0 }}
                          size={36}
                        >
                          <Sparkles size={20} color="white" />
                        </Avatar>
                      )}
                      
                      <div style={{ flex: 1 }}>
                        {renderMessageContent(message, index)}

                        {message.componentReferences && message.componentReferences.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                              {message.componentReferences.map((ref, i) => (
                                <span key={`comp-ref-${i}`}>
                                  {renderComponentReference(ref)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {message.fileReferences && message.fileReferences.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                              {message.fileReferences.map((ref, i) => (
                                <span key={`file-ref-${i}`}>
                                  {renderFileReference(ref)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {message.fileUploadRequests && message.fileUploadRequests.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                              {message.fileUploadRequests.map((request, i) => (
                                <span key={`file-upload-${i}`}>
                                  {renderComponentReference({
                                    ...request,
                                    type: 'fileUploadRequest'
                                  })}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {message.role === 'user' && (
                        <Avatar
                          style={{ backgroundColor: '#6200EE', flexShrink: 0 }}
                          size={36}
                        >
                          <User size={20} color="white" />
                        </Avatar>
                      )}
                    </div>
                  </Card>
                </List.Item>
              )}
            />
            <div ref={messagesEndRef} />
          </div>

          {/* Input area - fixed at bottom */}
          <div style={{ 
            padding: '12px 16px',
            backgroundColor: '#e5e5e5',
            borderTop: '1px solid #d9d9d9',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0
          }}>
            <TextArea
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              style={{ 
                flex: 1,
                borderRadius: '20px',
                padding: '10px 16px',
                fontSize: '14px',
                backgroundColor: 'white',
                border: '1px solid #d9d9d9',
                resize: 'none'
              }}
              disabled={sendingMessage || isLoading || uploadProcessing}
            />
            <Button 
              type="primary"
              onClick={handleSendMessage}
              style={{ 
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#6200EE',
                border: 'none',
                padding: 0
              }}
              loading={sendingMessage || isLoading || uploadProcessing}
              disabled={!newMessage.trim() || sendingMessage || isLoading || uploadProcessing}
            >
              {!sendingMessage && !isLoading && !uploadProcessing && <Send size={20} color="white" />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatComponentV2;